<template>
  <Modal :open="props.open" :hideClose="true">
    <template #modal-header>
      <div>
        <div class="flex items-center">
          <Icon name="Landmark" class="w-4 h-4 mr-1 stroke-info" />
          <div class="text-sm font-inter font-semibold text-info">Distribution Account</div>
        </div>
        <div
          class="font-league tracking-[-.56px] font-semibold text-[1.75rem]"
          style="color: var(--text-strongest, #000)"
        >
          Apply Bank Account to Investments
        </div>
      </div>
    </template>

    <template #modal-body>
      <div class="p-2 lg:p-6">
        <!-- Show which bank account is being applied -->
        <div class="bg-container-bg rounded-xl px-4 py-3 mb-5 flex items-center gap-3">
          <Icon name="Landmark" class="w-5 h-5 stroke-info shrink-0" />
          <div>
            <div class="font-inter text-sm font-semibold text-strongest">
              {{ selectedBank?.bank_name }} — {{ selectedBank?.account_type }}
            </div>
            <div class="font-inter text-xs">
              ····{{ selectedBank?.account_number?.slice(-4) || "****" }}
            </div>
          </div>
        </div>

        <div class="font-inter text-sm text-strong mb-3">
          Select which investments should use this account for distributions:
        </div>

        <!-- Select All -->
        <div
          v-if="eligibleInvestments.length > 1"
          class="bg-container-bg py-3 flex items-center px-3 mb-3 border-2 rounded-xl cursor-pointer"
          :class="allSelected ? 'border-radio-checkbox-outline' : 'border-container-bg'"
          @click="toggleSelectAll"
        >
          <div class="mr-3">
            <Icon
              name="CircleCheck"
              class="w-5 h-5 fill-radio-checkbox-checked dark:fill-white stroke-container-bg"
              v-if="allSelected"
            />
            <Icon name="Circle" class="w-5 h-5 stroke-radio-checkbox-outline" v-else />
          </div>
          <div class="font-inter text-sm font-semibold text-strongest">
            Select all ({{ eligibleInvestments.length }})
          </div>
        </div>

        <!-- Investment List -->
        <div class="space-y-2 max-h-[20rem] overflow-y-auto">
          <div
            v-for="investment in eligibleInvestments"
            :key="investment.record_id"
            class="flex items-center px-3 py-3 border-2 rounded-xl cursor-pointer"
            :class="
              selectedIds.has(investment.record_id)
                ? 'border-radio-checkbox-outline bg-info/5'
                : 'border-container-bg bg-container-bg'
            "
            @click="toggleInvestment(investment.record_id)"
          >
            <div class="mr-3 shrink-0">
              <Icon
                name="CircleCheck"
                class="w-5 h-5 fill-radio-checkbox-checked dark:fill-white stroke-container-bg"
                v-if="selectedIds.has(investment.record_id)"
              />
              <Icon name="Circle" class="w-5 h-5 stroke-radio-checkbox-outline" v-else />
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-inter text-sm font-semibold text-strongest truncate">
                {{ investment.name }}
              </div>
              <div class="font-inter text-xs mt-0.5">
                {{ investment.type }}
                <span v-if="investment.offering_name">
                  · {{ investment.offering_name }} · {{ investment.offering_type }} ·
                  {{ investment.record_id }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="eligibleInvestments.length === 0" class="text-center py-8 font-inter text-sm">
            All investments are already using this bank account.
          </div>
        </div>

        <!-- Error -->
        <div
          v-if="errorMessage"
          class="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl px-3 py-2.5 font-inter text-sm text-red-700 dark:text-red-300"
        >
          {{ errorMessage }}
        </div>
      </div>
    </template>

    <template #modal-footer>
      <div class="flex justify-between items-center w-full">
        <div class="font-inter text-xs text-secondary">
          <span v-if="selectedIds.size > 0">{{ selectedIds.size }} selected</span>
        </div>
        <div class="flex gap-2">
          <Button @click="emit('close-modal')" variant="paper-primary" size="sm">
            <template #label>{{ eligibleInvestments.length === 0 ? "Done" : "Close" }}</template>
          </Button>
          <Button
            v-if="eligibleInvestments.length > 0"
            @click="handleApply"
            variant="primary-grey"
            size="sm"
            :disabled="!canApply || isApplying"
          >
            <template #label>
              {{ isApplying ? "Applying..." : "Confirm" }}
            </template>
          </Button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { Modal, Icon, Button } from "@/components";
import { useCrudStore, useSchemaStore, useDataStore, useUIStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";

const crudStore = useCrudStore();
const dataStore = useDataStore();
const schemaStore = useSchemaStore();
const UIStore = useUIStore();
const analytics = useAnalytics();

const emit = defineEmits(["close-modal", "applied"]);

const props = defineProps({
  open: { type: Boolean, required: true },
  bank_account_id: { type: String, default: "" },
});

// --- State ---
const selectedIds = ref(new Set());
const isApplying = ref(false);
const errorMessage = ref("");

// --- Computed ---
const bankAccounts = computed(() => dataStore.bank_accounts || []);

const selectedBank = computed(() => {
  return bankAccounts.value.find((b) => b.record_id === props.bank_account_id) || null;
});

const eligibleInvestments = computed(() => {
  if (!dataStore.investments || !props.bank_account_id) return [];

  return dataStore.investments.filter((inv) => {
    if (!inv || typeof inv !== "object") return false;

    // Must have a signature date
    if (!inv.signature_date) return false;

    // Only include Individual-type investments (exclude Joint / Entity)
    const type = inv.type ?? "";
    if (type === "Joint" || type === "Entity") return false;

    // Exclude cancelled investments
    const status = inv.status ?? "";
    if (status === "Cancelled" || status === "Canceled") return false;

    // Include investments that either have no distribution account yet
    // OR whose current distribution account is different from the one
    // being applied — both cases benefit from this bank being set.
    if (inv.distribution_account === props.bank_account_id) return false;

    return true;
  });
});

const allSelected = computed(() => {
  return (
    eligibleInvestments.value.length > 0 &&
    selectedIds.value.size === eligibleInvestments.value.length
  );
});

const canApply = computed(() => {
  return props.bank_account_id !== "" && selectedIds.value.size > 0;
});

// --- Reset on open ---
watch(
  () => props.open,
  (is_open) => {
    if (is_open) {
      errorMessage.value = "";
      isApplying.value = false;

      // Pre-select investments that have no distribution account
      let unlinked = eligibleInvestments.value.filter((inv) => !inv.distribution_account);
      selectedIds.value = new Set(unlinked.map((inv) => inv.record_id));
    }
  }
);

// --- Actions ---
function toggleSelectAll() {
  if (allSelected.value) {
    selectedIds.value = new Set();
  } else {
    selectedIds.value = new Set(eligibleInvestments.value.map((inv) => inv.record_id));
  }
}

function toggleInvestment(record_id) {
  let next = new Set(selectedIds.value);
  if (next.has(record_id)) {
    next.delete(record_id);
  } else {
    next.add(record_id);
  }
  selectedIds.value = next;
}

async function handleApply() {
  if (!canApply.value || isApplying.value) return;

  isApplying.value = true;
  errorMessage.value = "";

  try {
    let successCount = 0;
    let failCount = 0;

    for (const investmentId of selectedIds.value) {
      try {
        let query = [
          {
            field_name: "record_id",
            operator: "eq",
            value: investmentId,
          },
        ];

        let update = {
          distribution_account: props.bank_account_id,
          distribution_method: "ACH",
        };
        update = schemaStore.addCreatedUpdatedFields(update, false, true);

        let response = await crudStore.update("investments", query, update);

        if (response.status === 200) {
          successCount++;
          await logActivity(investmentId, true);
        } else {
          failCount++;
          await logActivity(investmentId, false);
        }
      } catch (e) {
        failCount++;
        await logActivity(investmentId, false);
      }
    }

    if (successCount > 0) {
      UIStore.animateNotificationAlert({
        type: "success",
        message: `Bank account applied to ${successCount} investment${successCount !== 1 ? "s" : ""}.`,
      });

      analytics.bankAccountAppliedToInvestments(props.bank_account_id, successCount);
    }

    if (failCount > 0 && successCount === 0) {
      errorMessage.value = "Failed to apply bank account. Please try again.";
      return;
    } else if (failCount > 0) {
      errorMessage.value = `Applied to ${successCount}, but ${failCount} failed. Please retry.`;
      return;
    }

    emit("applied");
    emit("close-modal");
  } catch (e) {
    errorMessage.value = "An unexpected error occurred. Please try again.";
    console.error("Bulk apply failed", e);
  } finally {
    isApplying.value = false;
  }
}

async function logActivity(investmentId, success) {
  try {
    let log = {
      step: "Profile Page - Banking",
      substep: `Apply Bank Account to Investment ${investmentId}`,
      status: success ? "Completed" : "Failed",
      message: `Applied bank account ${props.bank_account_id} to investment ${investmentId}`,
      url: window.location.href,
      validation_details: null,
    };

    log = dataStore.setDefaultFields(log, "investoractivity");
    await crudStore.insert("investoractivity", [log]);
  } catch (e) {
    console.error("Failed to log activity", e);
  }
}
</script>
